import { API_BASE_URL } from "config";
import { StatusCodes } from "http-status-codes";
import {
  GetAssetStatusV1Input,
  GetAssetStatusV1Output,
  StartAssetReturnV1Input,
  StartAssetReturnV1Output,
} from "lib/types/app-link";

import { BaseService } from "./base-service";
import { CSRFService } from "./csrf-service";

/**
 * Service for handling app link operations including asset status checks and return processes
 */
export class AppLinkService extends BaseService {
  private csrfService: CSRFService;

  /**
   * Creates an instance of AppLinkService
   */
  constructor() {
    super(API_BASE_URL);
    this.csrfService = new CSRFService();
  }

  /**
   * Checks the license status for a given asset
   * @param getAssetStatusV1Input - Input parameters for asset status check
   * @returns Promise resolving to asset status information
   * @throws Error when the license status check fails
   */
  public async checkLicenseStatus(getAssetStatusV1Input: GetAssetStatusV1Input): Promise<GetAssetStatusV1Output> {
    const endpoint = "/ams/assets/status";
    const urlParams = this.buildGetAssetStatusUrlParams(getAssetStatusV1Input);

    const response = await this.fetch(`${endpoint}?${urlParams}`);

    if (response.status !== StatusCodes.OK) {
      throw new Error("An error occurred while checking the license cap.");
    }

    const jsonResponse = await response.json();

    return jsonResponse;
  }

  /**
   * Initiates the asset return process
   * @param startAssetReturnV1Input - Input parameters for starting asset return
   * @returns Promise resolving to asset return process information
   * @throws Error when the asset return process fails to start
   */
  public async startAssetReturnProcess(
    startAssetReturnV1Input: StartAssetReturnV1Input
  ): Promise<StartAssetReturnV1Output> {
    const endpoint = "/ams/assets/return";
    const body = JSON.stringify(startAssetReturnV1Input);

    const csrfToken = await this.csrfService.getToken();

    const response = await this.fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body,
    });

    if (response.status !== StatusCodes.OK) {
      throw new Error("An error occurred while starting the asset return process.");
    }

    const jsonResponse = await response.json();

    return jsonResponse;
  }

  /**
   * Builds URL parameters for asset status requests
   * @param getAssetStatusV1Input - Input parameters to convert to URL params
   * @returns URL-encoded parameter string
   */
  private buildGetAssetStatusUrlParams(getAssetStatusV1Input: GetAssetStatusV1Input): string {
    const urlParams = new URLSearchParams();

    urlParams.append("assetCategory", getAssetStatusV1Input.assetCategory!);
    urlParams.append("productName", getAssetStatusV1Input.productName!);

    if (getAssetStatusV1Input.instanceName) {
      urlParams.append("instanceName", getAssetStatusV1Input.instanceName);
    }

    return urlParams.toString();
  }
}
